import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import dataService from 'services/dataService';
import {
  // apiPatientChartSessionEnd,
  apiPatientChartSessionStart,
  apiPatientChartSessionPing,
  apiPatientChartSessionPauseUrl,
} from 'services/dataService/resources';

import { showResult } from '../utils';

const PING_INTERVAL = 10000; // 10 seconds
const IDLE_WARNING_SECONDS = 840; // 14 minutes
const IDLE_TIMEOUT_SECONDS = 900; // 15 minutes
const REFRESH_FLAG_KEY = 'patientChartRefresh';
export const IDLE_WARNING_NOTIFICATION_KEY = 'idle-warning';

const startPatientChartSession = async ({
  patientGuid,
}: {
  patientGuid: string;
}) => {
  const { data, error } = await dataService.createOne(
    apiPatientChartSessionStart(),
    {
      patientGuid,
    },
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

const pingPatientChartSession = async ({
  patientGuid,
  resetIdle,
}: {
  patientGuid: string;
  resetIdle?: boolean;
}) => {
  const { data, error } = await dataService.createOne(
    apiPatientChartSessionPing(),
    {
      patientGuid,
      ...(resetIdle && { resetIdle: true }),
    },
  );

  if (error) {
    console.error('Ping failed:', error);
    return null;
  }

  return data;
};

const pauseSession = (sessionId: string | null) => {
  if (sessionId) {
    navigator.sendBeacon(
      apiPatientChartSessionPauseUrl(),
      JSON.stringify({ sessionId, pauseReason: 'tab-close' }),
    );
  }
};

// const endSession = (sessionId: string | null) => {
//   if (sessionId) {
//     navigator.sendBeacon(
//       apiPatientChartSessionEnd(),
//       JSON.stringify({ sessionId, reasonEnded: 'navigate-away' }),
//     );
//   }
// };

export const usePatientChartSession = ({
  patientGuid,
  canRun,
  onTimeout,
}: {
  patientGuid: string;
  canRun: boolean;
  onTimeout?: () => void;
}) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [serverElapsedTime, setServerElapsedTime] = useState<number>(0);
  const [rpmTime, setRpmTime] = useState<number>(0);
  const [ccmTime, setCcmTime] = useState<number>(0);
  const [idleState, setIdleState] = useState<'warning' | 'timeout' | null>(
    null,
  );

  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionRef = useRef<string | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  const handlePingResultRef = useRef<((result: any) => void) | null>(null);
  const pendingActivityRef = useRef(false);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    sessionRef.current = sessionId;
  }, [sessionId]);

  const handlePingResult = (result: any) => {
    if (!result?.sessionId) return;
    setSessionId(result.sessionId);
    setServerElapsedTime(result.elapsedTime);
    setRpmTime(result.rpmTime);
    setCcmTime(result.ccmTime);

    const idle = result.idleSeconds ?? 0;
    if (idle >= IDLE_TIMEOUT_SECONDS) {
      setIdleState('timeout');
      onTimeoutRef.current?.();
    } else if (idle >= IDLE_WARNING_SECONDS) {
      setIdleState('warning');
    } else {
      setIdleState(null);
    }
  };

  handlePingResultRef.current = handlePingResult;

  const startPingingRef = useRef<(() => void) | null>(null);

  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: startPatientChartSession,
    onSuccess(data) {
      console.log('SESSION ID', data.sessionId, new Date());
      setSessionId(data.sessionId);
      setIdleState(null);
      pingPatientChartSession({ patientGuid }).then((r) =>
        handlePingResultRef.current?.(r),
      );
      startPingingRef.current?.();
    },
  });

  useEffect(() => {
    if (!patientGuid || !canRun) return;

    const startPinging = () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = setInterval(async () => {
        if (document.visibilityState === 'visible') {
          const resetIdle = pendingActivityRef.current;
          pendingActivityRef.current = false;
          const result = await pingPatientChartSession({
            patientGuid,
            resetIdle,
          });
          handlePingResultRef.current?.(result);
        }
      }, PING_INTERVAL);
    };

    startPingingRef.current = startPinging;

    const stopPinging = () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPinging();
        pingPatientChartSession({ patientGuid }).then((r) =>
          handlePingResultRef.current?.(r),
        );
      } else {
        stopPinging();
        if (sessionRef.current) {
          navigator.sendBeacon(
            apiPatientChartSessionPauseUrl(),
            JSON.stringify({
              sessionId: sessionRef.current,
              pauseReason: 'patient-switch',
            }),
          );
        }
      }
    };

    let isPageHide = false;
    const handlePageHide = () => {
      isPageHide = true;
      if (sessionRef.current) {
        sessionStorage.setItem(REFRESH_FLAG_KEY, patientGuid);
      }
      pauseSession(sessionRef.current);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    // Check if this is a refresh of the same chart
    const refreshPatientGuid = sessionStorage.getItem(REFRESH_FLAG_KEY);
    const isRefresh = refreshPatientGuid === patientGuid;
    sessionStorage.removeItem(REFRESH_FLAG_KEY);

    if (isRefresh) {
      // Refresh — skip start, preserve idle
      pingPatientChartSession({ patientGuid }).then((r) =>
        handlePingResultRef.current?.(r),
      );
      startPinging();
    } else {
      // Genuine navigation — call start to reset idle
      mutate({ patientGuid });
    }

    return () => {
      stopPinging();
      // Only pause on in-app navigation — pagehide already handles unload/refresh
      if (!isPageHide) {
        pauseSession(sessionRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [patientGuid, canRun]);

  return {
    startSession: mutate,
    isLoading,
    sessionId,
    serverElapsedTime,
    rpmTime,
    ccmTime,
    idleState,
    resetElapsedTime: () => setServerElapsedTime(0),
    onActivity: () => {
      pendingActivityRef.current = true;
    },
    ...other,
  };
};
